import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getActiveJobs } from '../services/jobService';
import { getSavedJobIds } from '../services/savedJobsService';
import { getProfileStats, getSeekerProfile } from '../services/seekerProfileService';
import { applyForJob, getUserApplications } from '../services/applicationService';
import { requestNotificationPermission } from '../services/fcmService';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import JobCard from '../components/JobCard';
import JobApplicationModal from '../components/JobApplicationModal';
import LoadingSpinner from '../components/LoadingSpinner';
import Footer from '../components/Footer';

const JobSeekerDashboard = ({ user, userData }) => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [showJobApplicationModal, setShowJobApplicationModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [applying, setApplying] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [locationInput, setLocationInput] = useState('');
  const [filters, setFilters] = useState({
    jobType: '',
    workMode: '',
    experienceLevel: '',
    location: '',
    search: ''
  });
  const [savedJobsCount, setSavedJobsCount] = useState(0);
  const [profileStats, setProfileStats] = useState({ completionPercentage: 0, isComplete: false, hasProfile: false });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalJobs, setTotalJobs] = useState(0);
  const [applicationsThisMonth, setApplicationsThisMonth] = useState(0);
  const jobsPerPage = 1000; // Show all jobs
  // Fresh student ID fetched directly from Firestore
  const [studentId, setStudentId] = useState(userData?.studentId || null);

  // Fetch / auto-generate Student ID fresh from Firestore
  useEffect(() => {
    const fetchStudentId = async () => {
      if (!user?.uid) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          if (data.studentId) {
            setStudentId(data.studentId);
          } else {
            // Auto-generate and save if missing
            const shortUid = user.uid.substring(0, 6).toUpperCase();
            const suffix = user.uid.slice(-4).toUpperCase();
            const newId = `STU-${shortUid}${suffix}`;
            await updateDoc(doc(db, 'users', user.uid), { studentId: newId });
            setStudentId(newId);
          }
        }
      } catch (err) {
        console.error('Error fetching student ID:', err);
      }
    };
    fetchStudentId();
  }, [user?.uid]);

  useEffect(() => {
    const init = async () => {
      await fetchJobs();
      setInitialLoading(false);
      if (user) {
        fetchSavedJobsCount();
        fetchProfileStats();
        fetchApplicationsThisMonth();
        initializeFCM();
      }
    };
    init();
  }, [user]);

  // Debounce location input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (locationInput !== filters.location) {
        handleFilterChange({ ...filters, location: locationInput });
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [locationInput]);

  const initializeFCM = async () => {
    try {
      // Check if FCM token exists, if not request permission and save
      const result = await requestNotificationPermission(user.uid);
      if (result.success) {
        console.log('FCM token initialized successfully');
      }
    } catch (error) {
      console.error('Error initializing FCM:', error);
    }
  };

  const fetchJobs = async (page = 1) => {
    console.log('🚀 [JobSeekerDashboard] fetchJobs called for page:', page);
    setLoading(true);
    try {
      console.log('🔄 [JobSeekerDashboard] Fetching active jobs...');

      const result = await getActiveJobs(filters, {
        limit: jobsPerPage,
        offset: (page - 1) * jobsPerPage
      });
      console.log('📊 [JobSeekerDashboard] Active jobs result:', result);

      if (result.success) {
        console.log('✅ [JobSeekerDashboard] Setting jobs:', result.jobs);
        setJobs(result.jobs);
        setTotalJobs(result.total || result.jobs.length);
        setCurrentPage(page);
      } else {
        console.error('❌ [JobSeekerDashboard] Failed to fetch active jobs:', result.error);
      }
    } catch (error) {
      console.error('❌ [JobSeekerDashboard] Error fetching jobs:', error);
      console.error('❌ [JobSeekerDashboard] Error stack:', error.stack);
    } finally {
      console.log('🏁 [JobSeekerDashboard] fetchJobs completed, setting loading to false');
      setLoading(false);
    }
  };

  const handleFilterChange = async (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
    setLoading(true);
    try {
      const result = await getActiveJobs(newFilters, {
        limit: jobsPerPage,
        offset: 0
      });
      if (result.success) {
        setJobs(result.jobs);
        setTotalJobs(result.total || result.jobs.length);
      }
    } catch (error) {
      console.error('Error filtering jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    handleFilterChange({ ...filters, search: searchTerm });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handlePageChange = (page) => {
    fetchJobs(page);
  };

  const totalPages = Math.ceil(totalJobs / jobsPerPage);

  const fetchSavedJobsCount = async () => {
    try {
      const result = await getSavedJobIds(user.uid);
      if (result.success) {
        setSavedJobsCount(result.jobIds.length);
      }
    } catch (error) {
      console.error('Error fetching saved jobs count:', error);
    }
  };

  // Store reference to the applying card callbacks
  const activeApplyCallbacks = React.useRef(null);

  const handleApplyClick = (job, setJobApplying, setJobApplied) => {
    setSelectedJob(job);
    activeApplyCallbacks.current = { setJobApplying, setJobApplied };
    setShowJobApplicationModal(true);
  };

  const handleConfirmApplication = async (applicationData) => {
    if (!selectedJob) return;

    setApplying(true);
    if (activeApplyCallbacks.current?.setJobApplying) {
      activeApplyCallbacks.current.setJobApplying(true);
    }
    try {
      const result = await applyForJob(selectedJob.id, user.uid, applicationData);

      if (result.success) {
        setShowJobApplicationModal(false);
        setSelectedJob(null);
        setShowSuccessAnimation(true);

        if (activeApplyCallbacks.current?.setJobApplied) {
          activeApplyCallbacks.current.setJobApplied(true);
        }

        // Hide success animation after 3 seconds
        setTimeout(() => {
          setShowSuccessAnimation(false);
        }, 3000);

        // Refresh jobs to update applied status and applications count
        fetchJobs();
        fetchApplicationsThisMonth();
      } else {
        alert(result.error || 'Failed to submit application');
      }
    } catch (error) {
      console.error('Error applying for job:', error);
      alert('Failed to submit application. Please try again.');
    } finally {
      setApplying(false);
      if (activeApplyCallbacks.current?.setJobApplying) {
        activeApplyCallbacks.current.setJobApplying(false);
      }
    }
  };

  const fetchProfileStats = async () => {
    try {
      const profileResult = await getSeekerProfile(user.uid);
      const statsResult = await getProfileStats(user.uid);

      setProfileStats({
        hasProfile: profileResult.success && profileResult.profile,
        completionPercentage: statsResult.success ? statsResult.stats.completionPercentage : 0,
        isComplete: statsResult.success ? statsResult.stats.completionPercentage >= 85 : false
      });
    } catch (error) {
      console.error('Error fetching profile stats:', error);
    }
  };

  const fetchApplicationsThisMonth = async () => {
    try {
      const result = await getUserApplications(user.uid);
      if (result.success) {
        // Get current month and year
        const now = new Date();
        const currentMonth = now.getMonth();
        const currentYear = now.getFullYear();

        // Filter applications from this month
        const thisMonthApplications = result.applications.filter(app => {
          const appliedDate = app.appliedAt;
          if (!appliedDate) return false;

          const appMonth = appliedDate.getMonth();
          const appYear = appliedDate.getFullYear();

          return appMonth === currentMonth && appYear === currentYear;
        });

        setApplicationsThisMonth(thisMonthApplications.length);
      }
    } catch (error) {
      console.error('Error fetching applications this month:', error);
    }
  };

  if (initialLoading) {
    console.log('🔄 [JobSeekerDashboard] Initial loading...');
    return <LoadingSpinner />;
  }

  console.log('🎨 [JobSeekerDashboard] Rendering with jobs:', jobs);
  console.log('🎨 [JobSeekerDashboard] Jobs length:', jobs.length);

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        {/* Hero Section */}
        <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600">
          <div className="absolute inset-0 bg-black opacity-10"></div>
          <div className="absolute inset-0">
            <div className="absolute top-0 left-0 w-72 h-72 bg-white opacity-10 rounded-full -translate-x-36 -translate-y-36"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-48 -translate-y-48"></div>
            <div className="absolute bottom-0 left-1/2 w-80 h-80 bg-white opacity-10 rounded-full translate-x-40 translate-y-40"></div>
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="text-center">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">
                Welcome back, <span className="text-yellow-300">{userData?.firstName}!</span>
              </h1>
              <p className="text-xl text-blue-100 mb-5 max-w-2xl mx-auto">
                Discover amazing opportunities and take the next step in your career journey
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/jobs')}
                  className="px-8 py-3 bg-white text-blue-600 font-semibold rounded-xl hover:bg-gray-100 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Browse Jobs
                </button>
                <button
                  onClick={() => navigate('/seeker-profile')}
                  className="px-8 py-3 bg-transparent border-2 border-white text-white font-semibold rounded-xl hover:bg-white hover:text-blue-600 transition-all duration-200"
                >
                  {!profileStats.hasProfile ? 'Create Profile' :
                    profileStats.completionPercentage < 85 ? 'Complete Profile' : 'Update Profile'}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Sidebar: Stats + Quick Actions */}
            <div className="lg:col-span-1 space-y-6">
              {/* Student ID Card */}
              {studentId && (
                <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-xl p-6 text-white">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" />
                        </svg>
                      </div>
                      <span className="text-sm font-semibold text-white text-opacity-90">Student ID Card</span>
                    </div>
                    <span className="text-xs bg-white bg-opacity-20 px-2 py-1 rounded-full">Active</span>
                  </div>
                  <div className="mt-1">
                    <p className="text-2xl font-bold tracking-widest font-mono">{studentId}</p>
                    <p className="text-sm text-indigo-200 mt-1">{userData?.firstName} {userData?.lastName}</p>
                    {userData?.franchiseId && <p className="text-xs text-indigo-300 mt-0.5">Franchise Member</p>}
                  </div>
                </div>
              )}
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                  </div>
                  Your Stats
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Applications Sent</p>
                        <p className="text-xs text-gray-500">This month</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">{applicationsThisMonth}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl border border-green-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Profile Views</p>
                        <p className="text-xs text-gray-500">This week</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-green-600">0</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Saved Jobs</p>
                        <p className="text-xs text-gray-500">Total saved</p>
                      </div>
                    </div>
                    <span className="text-2xl font-bold text-purple-600">{savedJobsCount}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl border border-orange-100">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-lg flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-600">Profile Complete</p>
                        <p className="text-xs text-gray-500">
                          {profileStats.isComplete ? 'Ready to apply' : 'Complete to apply'}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-2xl font-bold ${profileStats.isComplete ? 'text-green-600' : 'text-orange-600'}`}>
                        {profileStats.completionPercentage}%
                      </span>
                      {!profileStats.isComplete && (
                        <div className="w-16 bg-gray-200 rounded-full h-1.5 mt-1">
                          <div
                            className="bg-orange-500 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${profileStats.completionPercentage}%` }}
                          ></div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Quick Actions</h2>
                </div>
                <div className="grid grid-cols-1 gap-3">
                  {userData?.paymentId && (
                    <Link
                      to="/companies"
                      className="group flex items-center px-4 py-3 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl shadow-sm text-sm font-semibold text-blue-700 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                    >
                      <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                      </svg>
                      Browse Companies
                    </Link>
                  )}
                  <Link
                    to="/jobs"
                    className="group flex items-center px-4 py-3 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl shadow-sm text-sm font-semibold text-green-700 hover:from-green-100 hover:to-emerald-100 hover:border-green-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                    </svg>
                    Browse All Jobs
                  </Link>
                  <Link
                    to="/seeker-profile"
                    className="group flex items-center px-4 py-3 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl shadow-sm text-sm font-semibold text-purple-700 hover:from-purple-100 hover:to-pink-100 hover:border-purple-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    {!profileStats.hasProfile ? 'Create Profile' :
                      profileStats.completionPercentage < 85 ? 'Complete Profile' : 'Edit Profile'}
                  </Link>
                  <Link
                    to="/saved-jobs"
                    className="group flex items-center px-4 py-3 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-xl shadow-sm text-sm font-semibold text-red-700 hover:from-red-100 hover:to-pink-100 hover:border-red-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    Saved Jobs
                  </Link>
                  <Link
                    to="/application-history"
                    className="group flex items-center px-4 py-3 bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 rounded-xl shadow-sm text-sm font-semibold text-orange-700 hover:from-orange-100 hover:to-yellow-100 hover:border-orange-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    My Applications
                  </Link>
                  <Link
                    to="/notification-settings"
                    className="group flex items-center px-4 py-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl shadow-sm text-sm font-semibold text-indigo-700 hover:from-indigo-100 hover:to-purple-100 hover:border-indigo-300 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5"
                  >
                    <svg className="w-4 h-4 mr-3 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    Notification Settings
                  </Link>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Search and Filters */}
              <div className="bg-white rounded-2xl shadow-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Find Your Perfect Job</h2>
                </div>

                {/* Search Bar */}
                <div className="mb-6">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Search jobs by title, company, or keywords..."
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <button
                        onClick={handleSearch}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Job Type</label>
                    <select
                      value={filters.jobType}
                      onChange={(e) => handleFilterChange({ ...filters, jobType: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                    >
                      <option value="">All Job Types</option>
                      <option value="full-time">Full-time</option>
                      <option value="part-time">Part-time</option>
                      <option value="contract">Contract</option>
                      <option value="internship">Internship</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Work Mode</label>
                    <select
                      value={filters.workMode}
                      onChange={(e) => handleFilterChange({ ...filters, workMode: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                    >
                      <option value="">All Work Modes</option>
                      <option value="Work From Home">Work From Home</option>
                      <option value="In Field">In Field</option>
                      <option value="Work From Office">Work From Office</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Experience Level</label>
                    <select
                      value={filters.experienceLevel}
                      onChange={(e) => handleFilterChange({ ...filters, experienceLevel: e.target.value })}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white text-sm"
                    >
                      <option value="">All Experience Levels</option>
                      <option value="entry">Entry Level</option>
                      <option value="mid">Mid Level</option>
                      <option value="senior">Senior Level</option>
                      <option value="executive">Executive</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">Location</label>
                    <input
                      type="text"
                      placeholder="Enter city or remote"
                      value={locationInput}
                      onChange={(e) => setLocationInput(e.target.value)}
                      className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    />
                  </div>
                </div>
              </div>

              {/* Available Jobs */}
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 flex flex-col h-[600px]">
                <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                      </svg>
                    </div>
                    <h2 className="text-xl font-bold text-gray-900">Latest Opportunities</h2>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">{totalJobs} total jobs</span>
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                  </div>
                </div>

                {/* Scrollable Jobs Container */}
                <div className="flex-1 overflow-y-auto p-6 relative">
                  {loading && (
                    <div className="absolute inset-0 bg-white/50 backdrop-blur-sm flex items-center justify-center z-10">
                      <LoadingSpinner />
                    </div>
                  )}
                  {jobs.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-gray-400 mb-4">
                        <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2V6" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs found</h3>
                      <p className="text-gray-600 mb-4">Try adjusting your filters or check back later for new opportunities.</p>
                      <button
                        onClick={() => handleFilterChange({ jobType: '', workMode: '', experienceLevel: '', location: '' })}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                      >
                        Clear Filters
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {jobs.map((job) => (
                        <JobCard
                          key={job.id}
                          job={job}
                          showActions={false}
                          user={user}
                          userData={userData}
                          onJobSaved={() => fetchSavedJobsCount()}
                          onJobUnsaved={() => fetchSavedJobsCount()}
                          onApplyClick={handleApplyClick}
                        />
                      ))}
                    </div>
                  )}
                </div>

                {/* Show total jobs count */}
                <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm text-gray-600">
                    Showing {jobs.length} job{jobs.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Success Animation */}
      {showSuccessAnimation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 transition-opacity duration-300">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl transform transition-all duration-300 scale-100">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">🎉 Application Submitted!</h3>
            <p className="text-gray-600 mb-4">Your application has been sent successfully. Good luck!</p>
            <div className="flex items-center justify-center space-x-1">
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
              <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            </div>
          </div>
        </div>
      )}

      {/* Job Application Modal */}
      <JobApplicationModal
        job={selectedJob}
        isOpen={showJobApplicationModal}
        onClose={() => {
          setShowJobApplicationModal(false);
          setSelectedJob(null);
        }}
        onConfirm={handleConfirmApplication}
        applying={applying}
      />

      {/* Footer */}
      <Footer />
    </>
  );
};

export default JobSeekerDashboard;
